import { useEffect, useMemo, useState } from "react";
import { newProductPlanActions } from "redux/Ldb/actions/newProductPlanActions";
import { useAppDispatch, useAppSelector } from "../storeHooks";
import { NewProductPlanFilters } from "redux/Ldb/types/newProductPlanTypes";
import { useInfiniteScrollV2 } from "hooks/useInfiniteScrollV2";

const generateFilterKey = (filters?: NewProductPlanFilters): string => {
    if (!filters) return "default";

    // Create a stable string representation of the filters
    const filterEntries = Object.entries(filters)
        .filter(([_, value]) => value !== undefined && value !== null && value !== "")
        .sort(([a], [b]) => a.localeCompare(b));

    return filterEntries.length > 0
        ? filterEntries.map(([key, value]) => `${key}:${value}`).join("|")
        : "default";
};
const useGetNewProductPlans = (
    filters?: NewProductPlanFilters,
    options: {
        skip?: boolean;
        isInfiniteScroll?: boolean;
        scrollContentId?: string;
        pollingInterval?: number; //milliseconds
        refetchOnMount?: boolean;
    } = {}
) => {
    const {
        skip = false,
        isInfiniteScroll = false,
        pollingInterval = 0,
        scrollContentId,
        refetchOnMount = false,
    } = options;
    const dispatch = useAppDispatch();

    const filterKey = generateFilterKey(filters);

    const [isLoading, setIsLoading] = useState<Map<string, boolean>>(new Map([[filterKey, false]]));
    const [isFetching, setIsFetching] = useState<Map<string, boolean>>(
        new Map([[filterKey, false]])
    );
    const [isSuccess, setIsSuccess] = useState<Map<string, any>>(new Map([[filterKey, null]]));
    const [loadingIndex, setLoadingIndex] = useState<Map<string, number>>(
        new Map([[filterKey, 0]])
    );

    const {
        fetchingNewProductPlans,
        fetchedNewProductPlansSuccess,
        fetchedNewProductPlansFailure,
    } = useAppSelector((state) => state.newProductPlan);

    const [productPlansList, setProductPlansList] = useState<Map<string, { [key: string]: any }[]>>(
        new Map([[filterKey, []]])
    );
    const [pagination, setPagination] = useState<
        Map<string, { currentPage: string; noOfPages: string }>
    >(new Map([[filterKey, { currentPage: "1", noOfPages: "" }]]));

    useInfiniteScrollV2(
        useMemo(
            () => ({
                containerId: scrollContentId,
                onLoadMore: () => {
                    if (isInfiniteScroll) {
                        setPagination((prev) => {
                            const newMap = new Map(prev);
                            newMap.set(filterKey, {
                                ...prev.get(filterKey),
                                currentPage: String(Number(prev.get(filterKey)?.currentPage) + 1),
                            });
                            return newMap;
                        });
                    }
                },
                loading: isFetching.get(filterKey) || false,
                hasMore:
                    isSuccess.get(filterKey)?.data?.pagination?.current <
                    isSuccess.get(filterKey)?.data?.pagination?.number_of_pages,
                enabled: isInfiniteScroll,
                offset: 50,
            }),
            [
                scrollContentId,
                isFetching.get(filterKey),
                filterKey,
                isSuccess.get(filterKey),
                isInfiniteScroll,
            ]
        )
    );

    const handleFetchNewProductPlans = (params: NewProductPlanFilters) => {
        loadingIndex.get(filterKey) === 0 &&
            setIsLoading((prev) => {
                const newMap = new Map(prev);
                newMap.set(filterKey, true);
                return newMap;
            });

        setIsFetching((prev) => {
            const newMap = new Map(prev);
            newMap.set(filterKey, true);
            return newMap;
        });

        dispatch(newProductPlanActions.fetchNewProductPlans(params));
    };

    // Reset list and pagination when filters change
    useEffect(() => {
        if (!skip) {
            setProductPlansList(new Map([[filterKey, []]]));
            setPagination(new Map([[filterKey, { currentPage: "1", noOfPages: "" }]]));
            setIsFetching(new Map([[filterKey, false]]));
        }
    }, [filters, skip]);

    useEffect(() => {
        if (!skip) {
            const params = {
                ...filters,
                page: Number(pagination.get(filterKey)?.currentPage),
                limit: Number(pagination.get(filterKey)?.noOfPages),
            };
            handleFetchNewProductPlans(params);
        }
    }, [filters, skip, pagination]);

    useEffect(() => {
        if (Boolean(fetchedNewProductPlansSuccess)) {
            setProductPlansList((prev) => {
                const newMap = new Map(prev);
                const newProductPlans = fetchedNewProductPlansSuccess?.data?.productPlans || [];
                const updatedList = [...(prev.get(filterKey) || [])];

                // If polling interval is set, return the new product plans
                if (!!pollingInterval) {
                    newMap.set(filterKey, newProductPlans);
                    return newMap;
                }

                newProductPlans.forEach((newProduct) => {
                    const existingIndex = updatedList.findIndex(
                        (existing) => existing._id === newProduct._id
                    );

                    if (existingIndex !== -1) {
                        // Update existing item
                        updatedList[existingIndex] = newProduct;
                    } else {
                        // Add new item
                        updatedList.push(newProduct);
                    }
                });

                newMap.set(filterKey, updatedList);

                return newMap;
            });
            // dispatch(newProductPlanActions.resetFetchNewProductPlans());
        }
    }, [fetchedNewProductPlansSuccess]);

    const refetch = () => {
        const params = {
            ...filters,
            page: 1,
            limit: Number(pagination.get(filterKey)?.noOfPages),
        };
        handleFetchNewProductPlans(params);
    };

    useEffect(() => {
        if (!fetchingNewProductPlans && !!fetchedNewProductPlansSuccess) {
            loadingIndex.get(filterKey) === 1 &&
                setIsLoading((prev) => {
                    const newMap = new Map(prev);
                    newMap.set(filterKey, false);
                    return newMap;
                });
            setLoadingIndex((prev) => {
                const newMap = new Map(prev);
                newMap.set(filterKey, prev.get(filterKey) + 1);
                return newMap;
            });
        }
    }, [fetchingNewProductPlans, fetchedNewProductPlansSuccess]);

    useEffect(() => {
        if (!skip && pollingInterval) {
            const interval = setInterval(() => {
                refetch();
            }, pollingInterval);
            return () => clearInterval(interval);
        }
    }, [pollingInterval, skip]);

    useEffect(() => {
        setIsFetching((prev) => {
            const newMap = new Map(prev);
            newMap.set(filterKey, fetchingNewProductPlans);
            return newMap;
        });
    }, [fetchingNewProductPlans]);

    useEffect(() => {
        if (fetchedNewProductPlansFailure) {
            setIsFetching((prev) => {
                const newMap = new Map(prev);
                newMap.set(filterKey, false);
                return newMap;
            });
        }
    }, [fetchedNewProductPlansFailure]);

    useEffect(() => {
        setIsSuccess((prev) => {
            const newMap = new Map(prev);
            newMap.set(filterKey, fetchedNewProductPlansSuccess);
            return newMap;
        });
    }, [fetchedNewProductPlansSuccess]);

    console.log(
        "productPlansList",
        productPlansList,
        isLoading.get(filterKey),
        pagination.get(filterKey),
        isFetching.get(filterKey),
        isSuccess.get(filterKey)
    );

    return {
        isSuccess: Boolean(fetchedNewProductPlansSuccess),
        isError: Boolean(fetchedNewProductPlansFailure),
        isLoading: isLoading.get(filterKey) || false,
        isFetching: isFetching.get(filterKey) || false,
        data: refetchOnMount
            ? fetchedNewProductPlansSuccess?.data?.productPlans || []
            : productPlansList.get(filterKey) || [],
        error: fetchedNewProductPlansFailure,
        pagination: pagination.get(filterKey) || { currentPage: "1", noOfPages: "" },
        refetch,
    };
};

export default useGetNewProductPlans;
